import { useMemo, useCallback } from 'react';

import useSWRInfinite from 'swr/infinite';

import { fetchWithTokenAndRegion } from '@adsk/acs-sc-web-platform-api';
import { EntitlementsResponse } from '@adsk/alloy-provider';

type UseSearchParams = {
  search?: string;
  enabled?: boolean;
  getKey: (
    index: number,
    previousPageData: EntitlementsResponse,
    search?: string,
  ) => string | null | undefined;
};

/**
 * @param {UseSearchParams} search - search props
 * @returns {Object} - object with properties
 * @property {boolean} loading - is request loading
 * @property {Object} error - error object
 * @property {Object} data - data object
 * @property {number} pageSize - current page size
 * @property {function} setSize - function to change size
 * @property {function} nextPage - function to load next page
 * @property {boolean} hasNextPage - has next page
 * @property {function} retry - function to retry request
 */
const useSearch = ({ search, enabled, getKey }: UseSearchParams) => {
  const { data, error, mutate, setSize, size, isValidating } =
    useSWRInfinite<EntitlementsResponse>(
      (...swrAttrs) => enabled && getKey(...swrAttrs, search),
      fetchWithTokenAndRegion,
      {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
      },
    );

  const dataMemo = useMemo(
    () => data?.flatMap?.((page) => page.results),
    [data],
  );
  const hasNextPage = useMemo(
    () => !!data?.[size - 1]?.pagination?.nextUrl,
    [data, size],
  );
  const totalResults = useMemo(
    () => data?.[0]?.pagination?.totalResults,
    [data],
  );
  const nextPage = useCallback(() => setSize(size + 1), [setSize, size]);

  return {
    loading: isValidating,
    error,
    data: dataMemo,
    totalResults,
    pageSize: size,
    setPageSize: setSize,
    nextPage,
    hasNextPage,
    retry: mutate,
  };
};

export default useSearch;
